/**
 * A finger that lands on a caption belongs to the canvas under it (D340).
 *
 * The captions are buttons laid over the canvas (`Labels.tsx`), so a pointer that
 * lands on one never reaches the canvas's pointer handlers. For a mouse that is
 * right: a click on a caption selects its node, a double-click focuses it, an
 * ⌥-click folds it. For a finger it broke the gesture this decision exists for —
 * on a phone the captions cover much of a dense graph, so one finger of a pinch
 * landed on a caption about as often as not, the canvas saw a single pointer, and
 * the "pinch" panned. A pan begun on a caption did nothing at all.
 *
 * So a TOUCH press on a caption is handed to whichever canvas is up: replayed on
 * it as the same pointer, which the canvas then captures. From there it is one of
 * the canvas's own pointers — the lead of a pan, or one finger of a pinch — and
 * the canvas is the one that answers its tap: it asks `takeLabelTap` when the
 * press ends, and a tap that started on a caption selects that caption's node,
 * or with ⌥ folds it, whatever lies under the fingertip — the words were what
 * the reader aimed at. Where the browser still sends the tap's click to the
 * caption (it does when it resolves the tap against the element first touched,
 * as desktop Chrome with a touch screen does), the caption ignores a touch click
 * while a canvas is up (`canvasTookTouch`), so a tap is answered once — which a
 * ⌥-tap's fold, a toggle, needs. The double-click is the browser's to deliver,
 * to the caption or to the canvas, and either answers it once; see
 * `captionDoubleTap`.
 *
 * Mouse and pen are untouched. Pure plumbing: no store, one module-level target.
 */

/** The canvas that is up, if any. Each canvas registers itself while mounted. */
let target: HTMLCanvasElement | null = null

/** Touch presses that began on a caption, by pointer id, and its node. */
const begun = new Map<number, string>()

/**
 * Registers the canvas that takes caption touches. Returns the undo, which only
 * clears the slot if it still holds this canvas — the flat and the spatial view
 * hand over in one commit, and the leaving one's cleanup can run after the
 * arriving one registered.
 */
export function setTouchTarget(canvas: HTMLCanvasElement): () => void {
  target = canvas
  return () => {
    if (target !== canvas) return
    target = null
    // Every press remembered here was handed to the canvas that is leaving; none
    // can have reached the arriving one yet. Its ups will not come back through
    // a `takeLabelTap`, and a browser that reuses touch ids would read a stale
    // entry as a caption press on the next view.
    begun.clear()
    lastTap = null
  }
}

/**
 * Whether a caption should leave a click alone: a touch click while a canvas is
 * up, because the canvas already answered that tap (see the header). A click
 * that is not a PointerEvent says nothing about its pointer and is the caption's,
 * as every mouse and pen click is.
 */
export function canvasTookTouch(e: MouseEvent): boolean {
  return target !== null && (e as PointerEvent).pointerType === 'touch'
}

/**
 * Hands a touch press on a caption to the canvas. True when it did, and the
 * caption should do nothing more with it; false for any other pointer, or with
 * no canvas up, and the caption keeps the press.
 */
export function handToCanvas(e: PointerEvent, nodeId: string): boolean {
  if (e.pointerType !== 'touch' || !target) return false
  begun.set(e.pointerId, nodeId)
  target.dispatchEvent(
    new PointerEvent('pointerdown', {
      pointerId: e.pointerId,
      pointerType: 'touch',
      isPrimary: e.isPrimary,
      clientX: e.clientX,
      clientY: e.clientY,
      screenX: e.screenX,
      screenY: e.screenY,
      width: e.width,
      height: e.height,
      pressure: e.pressure,
      button: 0,
      buttons: 1,
      bubbles: true,
      cancelable: true,
    }),
  )
  return true
}

/**
 * How soon a second tap must follow for the browser's double-click to be the
 * caption's. Generous against the platforms' own windows (about 300 to 500 ms),
 * because a double-click that arrives at all is already the browser's verdict.
 */
export const CAPTION_DOUBLE_TAP_MS = 600

/** The last tap the canvas resolved as a caption's, and when. */
let lastTap: { id: string; at: number } | null = null

/**
 * Records the tap a canvas just resolved: a caption's node, or null for any other
 * tap, which forgets the one before it.
 */
export function noteCaptionTap(id: string | null, at: number = performance.now()): void {
  lastTap = id ? { id, at } : null
}

/**
 * The caption a double-click belongs to, if its second tap was a caption's and
 * was just now — and forgets it. A double-tap on a caption arrives at the canvas
 * as the canvas's double-click, since the press is the canvas's; without this it
 * hit-tested the fingertip, found the empty field beside the dot the words name,
 * and framed the whole graph where the reader had asked to focus a branch.
 */
export function captionDoubleTap(at: number = performance.now()): string | undefined {
  const t = lastTap
  lastTap = null
  return t && at - t.at <= CAPTION_DOUBLE_TAP_MS ? t.id : undefined
}

/**
 * Whether a press began on a caption, without forgetting it. The canvas reads it
 * as the press lands: a finger on the WORDS is never a stretch — the reader aimed
 * at the caption, not at the dot under it — so from a caption a drag pans.
 */
export function fromCaption(pointerId: number): boolean {
  return begun.has(pointerId)
}

/**
 * The node a press began on, if it began on a caption — and forgets it. Called
 * by the canvas on every pointer's end, so an entry never outlives its press,
 * whether that press was a tap, a pan, a pinch or cancelled.
 */
export function takeLabelTap(pointerId: number): string | undefined {
  const id = begun.get(pointerId)
  begun.delete(pointerId)
  return id
}
